import { useCallback, useEffect, useRef, useState } from 'react';
import { contentRect, toClient, toNormalized, type ContentRect } from './roiGeometry';
import { boxAtPoint, groundAnchor, projectPoints, type DetectionBox, type GeoFix } from './geolocate';

/**
 * Click a detected object to put it on the map.
 *
 * Clicking inside a detection box geolocates that box's ground-contact pixel; clicking bare
 * video geolocates the pixel itself, which is how anything the model does not know about
 * still gets placed. Either way the result is a proposal — App.tsx shows a preview pin with
 * its accuracy circle and the operator confirms it. Nothing is written from here.
 *
 * Deliberately manual. The demo box already manufactures around 200 incidents an hour; a map
 * point per detection would be far worse, and there is nothing to prune when a human writes
 * every row.
 */

const FALLBACK_FRAME = { width: 640, height: 360 };
const BOX_POLL_MS = 1000;

export interface PlacePickerProps {
  wrapperRef: React.RefObject<HTMLDivElement | null>;
  apiUrl: string;
  authHeaders: (json?: boolean) => HeadersInit;
  onClose: () => void;
  /** A successful fix, for the map to preview. */
  onFix: (fix: Extract<GeoFix, { ok: true }>, label: string) => void;
}

export default function PlacePicker({
  wrapperRef, apiUrl, authHeaders, onClose, onFix,
}: PlacePickerProps) {
  const [rect, setRect] = useState<ContentRect>({ left: 0, top: 0, width: 0, height: 0 });
  const [boxes, setBoxes] = useState<DetectionBox[]>([]);
  const [frame, setFrame] = useState(FALLBACK_FRAME);
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const svgRef = useRef<SVGSVGElement | null>(null);

  const measure = useCallback(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const img = wrapper.querySelector('img.video-stream') as HTMLImageElement | null;
    setRect(contentRect(
      wrapper.clientWidth, wrapper.clientHeight,
      img?.naturalWidth || FALLBACK_FRAME.width,
      img?.naturalHeight || FALLBACK_FRAME.height,
    ));
  }, [wrapperRef]);

  useEffect(() => {
    measure();
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const ro = new ResizeObserver(measure);
    ro.observe(wrapper);
    const poll = setInterval(measure, 1000);
    return () => { ro.disconnect(); clearInterval(poll); };
  }, [measure, wrapperRef]);

  // --- live boxes --------------------------------------------------------- //
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const res = await fetch(`${apiUrl}/api/drone/detection/boxes`, { headers: authHeaders() });
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        setBoxes(Array.isArray(data.boxes) ? data.boxes : []);
        // frame_shape is [h, w] — numpy order, not the width-first one you expect.
        if (Array.isArray(data.frame_shape) && data.frame_shape.length >= 2) {
          setFrame({ width: data.frame_shape[1], height: data.frame_shape[0] });
        }
      } catch { /* a dropped poll just means stale boxes for a second */ }
    };
    tick();
    const id = setInterval(tick, BOX_POLL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [apiUrl, authHeaders]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // --- the click ---------------------------------------------------------- //
  const handleClick = async (e: React.MouseEvent) => {
    const box = svgRef.current?.getBoundingClientRect();
    if (!box || busy) return;
    const [cu, cv] = toNormalized(e.clientX - box.left, e.clientY - box.top, rect);

    const hit = boxAtPoint(boxes, cu, cv, frame.width, frame.height);
    // A detection's ground contact is its bottom edge; a bare click is its own anchor.
    const [u, v] = hit ? groundAnchor(hit.bbox, frame.width, frame.height) : [cu, cv];
    const label = hit ? hit.class : 'Observed object';

    setBusy(true); setStatus(null);
    try {
      const [fix] = await projectPoints(apiUrl, authHeaders, [{ u, v, label }]);
      if (!fix) { setStatus('No answer from the backend'); return; }
      if (!fix.ok) { setStatus(fix.reason); return; }
      onFix(fix, label);
    } catch (err) {
      setStatus(err instanceof Error ? err.message : 'Could not geolocate');
    } finally {
      setBusy(false);
    }
  };

  const px = (bx: DetectionBox) => {
    const [x1, y1, x2, y2] = bx.bbox;
    const [ax, ay] = toClient(Math.min(x1, x2) / frame.width, Math.min(y1, y2) / frame.height, rect);
    const [bx2, by2] = toClient(Math.max(x1, x2) / frame.width, Math.max(y1, y2) / frame.height, rect);
    return { x: ax, y: ay, w: bx2 - ax, h: by2 - ay };
  };

  return (
    <div style={{ position: 'absolute', inset: 0, zIndex: 6 }}>
      <svg
        ref={svgRef}
        style={{
          position: 'absolute', inset: 0, width: '100%', height: '100%',
          cursor: busy ? 'progress' : 'crosshair',
        }}
        onClick={handleClick}
      >
        {boxes.map((b, i) => {
          const r = px(b);
          const [fx, fy] = (() => {
            const [gu, gv] = groundAnchor(b.bbox, frame.width, frame.height);
            return toClient(gu, gv, rect);
          })();
          return (
            <g key={i} pointerEvents="none">
              <rect x={r.x} y={r.y} width={r.w} height={r.h}
                    fill="rgba(0,200,255,0.10)" stroke="#00c8ff" strokeWidth={1.5} />
              {/* The dot is the pixel that would actually be geolocated. Showing it stops
                  the operator wondering why a tall box lands where it does. */}
              <circle cx={fx} cy={fy} r={3.5} fill="#ffd166" stroke="#000" strokeWidth={0.75} />
            </g>
          );
        })}
      </svg>

      <div style={{
        position: 'absolute', top: 8, left: 8, display: 'flex', gap: 6,
        alignItems: 'center', flexWrap: 'wrap', maxWidth: 'calc(100% - 16px)',
      }}>
        <span style={{
          background: 'rgba(0,0,0,0.72)', color: 'var(--color-warning)', fontSize: 11,
          padding: '3px 8px', borderRadius: 3, letterSpacing: '0.05em',
        }}>
          {busy ? 'PLACING…' : 'PLACE — click an object, or anywhere on the ground'}
        </span>
        <button
          style={{
            background: 'rgba(0,0,0,0.6)', color: '#fff',
            border: '1px solid rgba(255,255,255,0.35)', borderRadius: 3,
            padding: '3px 9px', fontSize: 11, cursor: 'pointer', letterSpacing: '0.04em',
          }}
          onClick={onClose}
        >CLOSE</button>
        {status && (
          <span style={{
            background: 'rgba(120,0,0,0.78)', color: '#ffd7d7', fontSize: 11,
            padding: '3px 8px', borderRadius: 3, maxWidth: 380,
          }}>{status}</span>
        )}
      </div>
    </div>
  );
}
